/**
 * Shared POS calculation utilities.
 * Used by both backend (NestJS) and frontend (React) to ensure consistent arithmetic.
 *
 * PRINCIPLES:
 * 1. unitPrice is ALWAYS per-pack (pack qty × per-unit purchase cost)
 * 2. All batch stock quantities are tracked in BASE units
 * 3. Discount reduces taxable value BEFORE GST (Section 15, CGST Act)
 * 4. All monetary values rounded to 2 decimal places
 */

// ─── Types ───────────────────────────────────────────────────────────
export interface LineItemInput {
  /** Quantity in purchase/sale packs */
  quantity: number;
  /** Units per pack (1 for base unit) */
  unitsPerPack: number;
  /** Price per pack */
  unitPrice: number;
  /** Trade discount percent on this line */
  discountPercent?: number;
  /** Scheme/cash discount percent (applied after trade discount) */
  schemeDiscountPercent?: number;
  /** GST percent */
  taxPercent?: number;
  /** Free quantity in packs */
  freeQuantity?: number;
}

export interface LineItemTotals {
  /** quantity + freeQuantity (total received in packs) */
  receivedQty: number;
  /** quantity converted to base units: quantity × unitsPerPack */
  baseQuantity: number;
  /** (quantity + freeQuantity) × unitsPerPack */
  totalBaseQuantity: number;
  /** quantity × unitPrice (before discount) */
  subtotal: number;
  /** subtotal × discountPercent / 100 */
  discountAmount: number;
  /** (subtotal - discountAmount) × schemeDiscountPercent / 100 */
  schemeDiscountAmount: number;
  /** subtotal − discountAmount − schemeDiscountAmount */
  taxableAmount: number;
  /** taxableAmount × taxPercent / 100 */
  taxAmount: number;
  /** taxableAmount + taxAmount */
  totalAmount: number;
}

export interface GstSplit {
  gstAmount: number;
  igst: number;
  cgst: number;
  sgst: number;
}

export interface BillDiscountResult {
  /** Actual discount value (absolute) */
  billDiscountAmount: number;
  /** Adjusted subtotal after bill discount */
  adjustedSubtotal: number;
  /** GST (unchanged — discount applied post-GST to gross total) */
  adjustedTotalGst: number;
  /** Final grand total (grossTotal − billDiscountAmount) */
  baseGrandTotal: number;
}

// ─── Rounding Helper ─────────────────────────────────────────────────
export const round2 = (n: number): number => Number(n.toFixed(2));

// ─── Line-Level Calculations ─────────────────────────────────────────

/**
 * Calculate totals for a purchase/GRN line item.
 * unitPrice is per-pack, so subtotal = quantity × unitPrice.
 */
export function calculateLineItemTotals(item: LineItemInput): LineItemTotals {
  const qty = item.quantity;
  const freeQty = item.freeQuantity || 0;
  const unitsPerPack = item.unitsPerPack || 1;
  const discountPercent = item.discountPercent || 0;
  const schemeDiscountPercent = item.schemeDiscountPercent || 0;
  const taxPercent = item.taxPercent || 0;

  const receivedQty = qty + freeQty;
  const baseQuantity = qty * unitsPerPack;
  const totalBaseQuantity = receivedQty * unitsPerPack;
  const subtotal = qty * item.unitPrice;
  const discountAmount = round2((subtotal * discountPercent) / 100);
  const afterTradeDiscount = round2(subtotal - discountAmount);
  const schemeDiscountAmount = round2(
    (afterTradeDiscount * schemeDiscountPercent) / 100,
  );
  const taxableAmount = round2(afterTradeDiscount - schemeDiscountAmount);
  const taxAmount = round2((taxableAmount * taxPercent) / 100);
  const totalAmount = round2(taxableAmount + taxAmount);

  return {
    receivedQty,
    baseQuantity,
    totalBaseQuantity,
    subtotal: round2(subtotal),
    discountAmount,
    schemeDiscountAmount,
    taxableAmount,
    taxAmount,
    totalAmount,
  };
}

/**
 * Calculate return line totals (purchase return or sales return).
 * unitPrice should be per-base-unit (not per-pack).
 */
export function calculateReturnLineTotals(
  qtyReturned: number,
  unitPrice: number,
  taxPercent: number,
  discountPercent: number,
  schemeDiscountPercent: number = 0,
): {
  lineSubtotal: number;
  lineDiscount: number;
  lineSchemeDiscount: number;
  taxAmount: number;
  lineTotal: number;
} {
  const lineSubtotal = qtyReturned * unitPrice;
  const lineDiscount = round2((lineSubtotal * discountPercent) / 100);
  const afterTradeDiscount = round2(lineSubtotal - lineDiscount);
  const lineSchemeDiscount = round2(
    (afterTradeDiscount * schemeDiscountPercent) / 100,
  );
  const taxableAmount = round2(afterTradeDiscount - lineSchemeDiscount);
  const taxAmount = round2((taxableAmount * taxPercent) / 100);
  const lineTotal = round2(taxableAmount + taxAmount);

  return {
    lineSubtotal: round2(lineSubtotal),
    lineDiscount,
    lineSchemeDiscount,
    taxAmount,
    lineTotal,
  };
}

// ─── GST Split ───────────────────────────────────────────────────────

/**
 * Split GST amount into IGST or CGST+SGST depending on inter-/intra-state.
 */
export function calculateGstSplit(
  taxableAmount: number,
  gstPercent: number,
  isInterState: boolean,
  options?: { isExempted?: boolean },
): GstSplit {
  if (options?.isExempted || gstPercent <= 0) {
    return { gstAmount: 0, igst: 0, cgst: 0, sgst: 0 };
  }

  const gstAmount = round2((taxableAmount * gstPercent) / 100);

  if (isInterState) {
    return { gstAmount, igst: gstAmount, cgst: 0, sgst: 0 };
  }

  const halfGst = round2(gstAmount / 2);
  return { gstAmount, igst: 0, cgst: halfGst, sgst: halfGst };
}

// ─── Bill-Level Discount ─────────────────────────────────────────────

/**
 * Apply bill-level discount to the gross total (post-GST).
 *
 * Bill discounts in a pharmacy POS are cash/invoice discounts given at the
 * time of billing — they are NOT trade discounts under Section 15 CGST Act.
 * Reducing the taxable base (pre-GST) would silently shrink the GST amount,
 * making the displayed breakdown (Subtotal + GST − Disc = Total) fail to
 * balance. Instead we subtract the discount from the gross total directly
 * so that ₹1 bill discount always reduces the payable amount by exactly ₹1
 * and the on-screen arithmetic is always self-consistent.
 *
 * @param subtotal    Sum of line taxable amounts (after line discounts, before GST)
 * @param totalGst    Sum of line GST amounts
 * @param discountType 'PERCENTAGE' | 'FLAT'
 * @param discountValue Percent or absolute value
 */
export function applyBillDiscount(
  subtotal: number,
  totalGst: number,
  discountType: "PERCENTAGE" | "FLAT" | string,
  discountValue: number,
): BillDiscountResult {
  const grossTotal = round2(subtotal + totalGst);

  const billDiscountAmount =
    discountType === "PERCENTAGE"
      ? round2((grossTotal * discountValue) / 100)
      : round2(Math.min(discountValue, grossTotal));

  const baseGrandTotal = round2(Math.max(0, grossTotal - billDiscountAmount));

  // GST is NOT reduced — the discount comes off the final payable amount
  const adjustedTotalGst = totalGst;
  const adjustedSubtotal = round2(
    Math.max(0, baseGrandTotal - adjustedTotalGst),
  );

  return {
    billDiscountAmount: round2(billDiscountAmount),
    adjustedSubtotal,
    adjustedTotalGst,
    baseGrandTotal,
  };
}

// ─── Auto Round-Off ──────────────────────────────────────────────────

/**
 * Calculate auto round-off to nearest ₹1.
 */
export function autoRoundOff(amount: number): number {
  return round2(Math.round(amount) - amount);
}

// ─── Price with Price List ───────────────────────────────────────────

/**
 * Apply price list margin or discount to a base price.
 */
export function applyPriceList(
  basePrice: number,
  priceList: { marginPercent?: number; discountPercent?: number } | null,
): number {
  if (!priceList) return basePrice;
  if (priceList.marginPercent) {
    return round2(basePrice * (1 + priceList.marginPercent / 100));
  }
  if (priceList.discountPercent) {
    return round2(basePrice * (1 - priceList.discountPercent / 100));
  }
  return basePrice;
}
